import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';
import {
  IconCloudCog,
  IconLayoutDashboard,
  IconActivity,
  IconPlus,
  IconReportAnalytics,
  IconChartHistogram,
  IconFileInvoice,
  IconTool,
  IconUserCog,
  IconNews,
  IconLifebuoy,
  IconTruck
} from '@tabler/icons-react';

import axiosServices, { fetcher } from 'utils/axios';
import { NavItemType } from 'types';
import useTenantScopedKey from 'hooks/useTenantScopedKey';
import { currentTenantScopedKey } from 'utils/tenantCacheKey';

export interface ModulePageResponse {
  modulePageId: number;
  moduleId: number;
  parentModulePageId?: number | null;
  pageKey: string;
  title: string;
  navType: string;
  url?: string | null;
  iconKey?: string | null;
  breadcrumbs: boolean;
  sortOrder: number;
  modulePageStatusId: number;
  isVisible: boolean;
  isEnabled: boolean;
  metadata?: string | null;
}

export interface UpdateModulePageRequest {
  moduleId: number;
  parentModulePageId?: number | null;
  pageKey: string;
  title: string;
  navType: string;
  url?: string | null;
  iconKey?: string | null;
  breadcrumbs: boolean;
  sortOrder: number;
  modulePageStatusId: number;
  isVisible: boolean;
  isEnabled: boolean;
  metadata?: string | null;
}

const endpoints = {
  modulePage: 'api/module-pages'
};

export const MODULE_PAGE_ICON_OPTIONS = [
  'IconCloudCog',
  'IconLayoutDashboard',
  'IconActivity',
  'IconPlus',
  'IconReportAnalytics',
  'IconChartHistogram',
  'IconFileInvoice',
  'IconTool',
  'IconUserCog',
  'IconNews',
  'IconLifebuoy',
  'IconTruck'
];

const iconByKey: Record<string, NavItemType['icon']> = {
  iconcloudcog: IconCloudCog,
  cloudcog: IconCloudCog,
  iconlayoutdashboard: IconLayoutDashboard,
  dashboard: IconLayoutDashboard,
  iconactivity: IconActivity,
  activity: IconActivity,
  iconplus: IconPlus,
  plus: IconPlus,
  iconreportanalytics: IconReportAnalytics,
  reports: IconReportAnalytics,
  iconcharthistogram: IconChartHistogram,
  chart: IconChartHistogram,
  iconfileinvoice: IconFileInvoice,
  invoice: IconFileInvoice,
  icontool: IconTool,
  tool: IconTool,
  maintenance: IconTool,
  iconusercog: IconUserCog,
  usercog: IconUserCog,
  admin: IconUserCog,
  iconnews: IconNews,
  news: IconNews,
  cms: IconNews,
  iconlifebuoy: IconLifebuoy,
  support: IconLifebuoy,
  icontruck: IconTruck,
  truck: IconTruck
};

function normalizeKey(value?: string | null): string {
  if (!value) {
    return '';
  }

  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function normalizeNavType(value?: string): NavItemType['type'] {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'group' || normalized === 'collapse' || normalized === 'item') {
    return normalized;
  }

  return 'item';
}

function mapIcon(iconKey?: string | null): NavItemType['icon'] | undefined {
  return iconByKey[normalizeKey(iconKey)];
}

function toNavItem(page: ModulePageResponse): NavItemType {
  return {
    id: page.pageKey || `module-page-${page.modulePageId}`,
    title: page.title,
    type: normalizeNavType(page.navType),
    ...(page.url && { url: page.url }),
    ...(mapIcon(page.iconKey) && { icon: mapIcon(page.iconKey) }),
    breadcrumbs: page.breadcrumbs,
    disabled: !page.isEnabled,
    children: []
  };
}

function normalizePayload(payload: unknown): ModulePageResponse[] {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload as ModulePageResponse[];
  }

  if (typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const envelopeList = (record.items ?? record.modulePages ?? record.data) as unknown;
  if (Array.isArray(envelopeList)) {
    return envelopeList as ModulePageResponse[];
  }

  const single = (record.modulePage ?? record.data ?? payload) as ModulePageResponse;
  if (single && typeof single === 'object' && 'modulePageId' in single) {
    return [single];
  }

  return [];
}

function finalizeNode(node: NavItemType): NavItemType {
  const children = (node.children ?? []).map(finalizeNode);

  if (children.length === 0) {
    const leaf = { ...node };
    delete leaf.children;
    return leaf;
  }

  const type = node.type === 'group' ? 'group' : 'collapse';
  return {
    ...node,
    type,
    children
  };
}

function buildNavItemType(payload: unknown): NavItemType | undefined {
  const pages = normalizePayload(payload)
    .filter((page) => page.isVisible)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (pages.length === 0) {
    return undefined;
  }

  const navById = new Map<number, NavItemType>();
  pages.forEach((page) => {
    navById.set(page.modulePageId, toNavItem(page));
  });

  const roots: NavItemType[] = [];
  pages.forEach((page) => {
    const node = navById.get(page.modulePageId);
    if (!node) {
      return;
    }

    const parentId = page.parentModulePageId;
    if (parentId == null) {
      roots.push(node);
      return;
    }

    const parent = navById.get(parentId);
    if (!parent) {
      roots.push(node);
      return;
    }

    parent.children = [...(parent.children ?? []), node];
  });

  const finalizedRoots = roots.map(finalizeNode);

  if (finalizedRoots.length === 1 && finalizedRoots[0].type === 'group') {
    return finalizedRoots[0];
  }

  return {
    id: 'module-pages',
    title: '',
    type: 'group',
    children: finalizedRoots
  };
}

function extractNavItemType(payload: unknown): NavItemType | undefined {
  return buildNavItemType(payload);
}

function toUpdateRequest(page: ModulePageResponse): UpdateModulePageRequest {
  return {
    moduleId: page.moduleId,
    parentModulePageId: page.parentModulePageId ?? null,
    pageKey: page.pageKey,
    title: page.title,
    navType: page.navType,
    url: page.url ?? null,
    iconKey: page.iconKey ?? null,
    breadcrumbs: page.breadcrumbs,
    sortOrder: page.sortOrder,
    modulePageStatusId: page.modulePageStatusId,
    isVisible: page.isVisible,
    isEnabled: page.isEnabled,
    metadata: page.metadata ?? null
  };
}

export class ModulePageApi {
  static async getNavItemType(): Promise<NavItemType | undefined> {
    const data = await fetcher(endpoints.modulePage);
    return extractNavItemType(data);
  }
}

export async function updateModulePage(modulePageId: number, payload: UpdateModulePageRequest): Promise<ModulePageResponse> {
  const response = await axiosServices.put<ModulePageResponse>(`${endpoints.modulePage}/${modulePageId}`, payload);
  return response.data;
}

export async function saveModulePages(pages: ModulePageResponse[]): Promise<ModulePageResponse[]> {
  if (pages.length === 0) {
    return [];
  }

  return Promise.all(pages.map((page) => updateModulePage(page.modulePageId, toUpdateRequest(page))));
}

export function setModulePagesCache(pages: ModulePageResponse[]) {
  return mutate(currentTenantScopedKey(endpoints.modulePage), pages, { revalidate: false });
}

export function revalidateModulePages() {
  const separator = endpoints.modulePage.includes('?') ? '&' : '?';
  const url = `${endpoints.modulePage}${separator}_ts=${Date.now()}`;
  return mutate(currentTenantScopedKey(endpoints.modulePage), fetcher(url), { populateCache: true, revalidate: false });
}

export function useGetActiveModulePageNavItems() {
  const key = useTenantScopedKey(endpoints.modulePage);
  const { data, isLoading, error, isValidating } = useSWR<unknown>(key, () => fetcher(endpoints.modulePage), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      modulePageNavItems: extractNavItemType(data),
      modulePageNavItemLoading: isLoading,
      // No answer yet for the selected tenant. Unlike isLoading this holds through the gaps — the
      // key changing before its fetch starts, and the tenant-change sweep clearing the entry — so a
      // caller holding on to the previous tenant's menu knows to keep holding.
      modulePageNavItemPending: key != null && data === undefined && !error,
      modulePageNavItemError: error,
      modulePageNavItemValidating: isValidating
    }),
    [data, error, isLoading, isValidating, key]
  );

  return memoizedValue;
}

export function useGetModulePages() {
  const { data, isLoading, error, isValidating } = useSWR<unknown>(
    useTenantScopedKey(endpoints.modulePage),
    () => fetcher(endpoints.modulePage),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  return useMemo(
    () => ({
      modulePages: normalizePayload(data).sort((a, b) => a.sortOrder - b.sortOrder),
      modulePagesLoading: isLoading,
      modulePagesError: error,
      modulePagesValidating: isValidating
    }),
    [data, error, isLoading, isValidating]
  );
}
